/**
 * `Alert` — messaggio contestuale (errore/successo/info/avviso).
 *
 * Per gli errori usa `role="alert"` (annuncio assertivo); per gli altri toni
 * `role="status"` (annuncio polite). Il `role` può essere sovrascritto via prop
 * per casi specifici.
 */
import type { ReactNode } from 'react';
import { Icon } from './Icon';

export type AlertTone = 'error' | 'success' | 'info' | 'warning';

interface AlertProps {
  tone?: AlertTone;
  role?: 'alert' | 'status';
  className?: string;
  children: ReactNode;
  /**
   * Se fornita, l'alert mostra una "X" per chiuderlo e invoca questa callback
   * al click. Chi la passa è responsabile di nascondere l'alert (es. azzerando
   * lo stato che lo rende visibile).
   */
  onDismiss?: () => void;
  /** Etichetta accessibile del pulsante di chiusura (default: "Chiudi"). */
  dismissLabel?: string;
}

const TONE_CLASS: Record<AlertTone, string> = {
  error: 'ui-alert--error',
  success: 'ui-alert--success',
  info: 'ui-alert--info',
  warning: 'ui-alert--warning',
};

const TONE_ICON: Record<AlertTone, string> = {
  error: '⚠',
  success: '✓',
  info: 'ℹ',
  warning: '⚠',
};

export function Alert({
  tone = 'info',
  role,
  className,
  children,
  onDismiss,
  dismissLabel = 'Chiudi',
}: AlertProps) {
  const resolvedRole = role ?? (tone === 'error' ? 'alert' : 'status');
  const classes = ['ui-alert', TONE_CLASS[tone], className ?? '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={classes} role={resolvedRole}>
      <span aria-hidden="true">{TONE_ICON[tone]}</span>
      <span className="ui-alert__content">{children}</span>
      {onDismiss !== undefined && (
        <button
          type="button"
          className="ui-alert__dismiss"
          aria-label={dismissLabel}
          onClick={onDismiss}
        >
          <Icon name="close" />
        </button>
      )}
    </div>
  );
}
